import * as React from 'react';
import type { CustomColumnProps, CustomRowProps } from '../../../components/table/types';
import type { TopicFocusEntryInterface } from '@whizz/api-model';
import { useI18n } from '@whizz/react-i18n';
import { Table } from '../../../components';
import { createGlobalStyle } from 'styled-components';
import { Progressions } from './';
import { EMPTY_CELL_CONTENTS } from '../../../util/vars';
import { DateTime } from 'luxon';
import { round } from '../../../util/numbers';
import { valueOrDash } from '../../../util/strings';
import { APP_THEME } from '../../../styles/theme/styled/theme';

export type TopicFocusTableAccessors =
  | 'name'
  | 'topic'
  | 'start'
  | 'end'
  | 'progressions'
  | 'usage'
  | 'active'
  | 'startts'
  | 'endts';

const GlobalStyle = createGlobalStyle`
  table {
    [data-id='col_name'],
    [data-id='col_topic'],
    [data-id='col_start'],
    [data-id='col_end'],
    [data-id='col_progressions'],
    [data-id='col_usage'] {
      div {
        justify-content: start !important;
      }
    }
  }
`;

interface Props {
  data: TopicFocusEntryInterface[];
}

const TopicFocusTable = ({ data }: Props): JSX.Element => {
  const { translate, locale } = useI18n();
  // Todo: fix rowA and rowB types
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sortBy = (rowA: any, rowB: any, id: string, desc: boolean) => {
    let a = rowA.original[id];
    let b = rowB.original[id];

    switch (id) {
      case 'start':
        a = rowA.original.startts;
        b = rowB.original.startts;
        break;
      case 'end':
        a = rowA.original.endts;
        b = rowB.original.endts;
        break;
    }

    if (EMPTY_CELL_CONTENTS.includes(a)) {
      if (EMPTY_CELL_CONTENTS.includes(b)) {
        return 0;
      }

      return desc ? -1 : 1;
    }

    if (EMPTY_CELL_CONTENTS.includes(b)) {
      return desc ? 1 : -1;
    }

    return a > b
      ? 1
      : b > a
        ? -1
        : 0
    ;
  };

  const columns = [
    {
      Header: translate('pages.TOPIC.HEADERS.NAME'),
      accessor: 'name',
      sortType: sortBy,
    },
    {
      Header: translate('pages.TOPIC.HEADERS.TOPIC'),
      accessor: 'topic',
      sortType: sortBy,
    },
    {
      Header: translate('pages.TOPIC.HEADERS.START'),
      accessor: 'start',
      sortType: sortBy,
    },
    {
      Header: translate('pages.TOPIC.HEADERS.END'),
      accessor: 'end',
      sortType: sortBy,
    },
    {
      Header: translate('pages.TOPIC.HEADERS.PROGRESSIONS'),
      accessor: 'progressions',
      sortType: sortBy,
    },
    {
      Header: translate('pages.TOPIC.HEADERS.USAGE'),
      accessor: 'usage',
      sortType: sortBy,
    },
    {
      id: 'active',
      accessor: 'active',
    },
    {
      id: 'startts',
      accessor: 'startTs',
    },
    {
      id: 'endts',
      accessor: 'endts',
    },
  ] as CustomColumnProps<TopicFocusTableAccessors>[];

  const getFormattedData = (): CustomRowProps<TopicFocusTableAccessors>[]  => {
    return data.map((entry) => {
      const topicFocus = entry.topicFocus;
      const { topic, progress: topicFocusProgress, startDate, endDate, usage } = topicFocus?.current ?? topicFocus?.previous ?? {};
      const progress = topicFocusProgress ? Number(topicFocusProgress.split('/')[0]) : null;

      const startDateDateTime = null != startDate ? DateTime.fromISO(startDate as string).toUTC() : null;
      const endDateDateTime = null != endDate ? DateTime.fromISO(endDate as string).toUTC() : null;

      const active = null != endDateDateTime && DateTime.now() < endDateDateTime && null != progress && 3 > progress;

      return {
        name: `${entry.firstname} ${entry.lastname}`,
        topic: topic ?? translate('pages.TOPIC.NONE'),
        start: valueOrDash(startDateDateTime?.toLocaleString(undefined, locale ? { locale: locale.getId() } : undefined)),
        startts: startDateDateTime?.toMillis(),
        end: valueOrDash(endDateDateTime?.toLocaleString(undefined, locale ? { locale: locale.getId() } : undefined)),
        endts: endDateDateTime?.toMillis(),
        progressions: null != progress ? (<Progressions value={progress} active={active} />) : valueOrDash(progress),
        usage: valueOrDash(usage ? round(usage / 60, 0) : usage),
        active: active,
      };
    });
  };

  return (
    <>
      <GlobalStyle />
      <Table
        id={'topic-focus-table'}
        showTbodyBorder
        cols={columns}
        content={getFormattedData()}
        initialState={{
          sortBy: [
            {
              id: 'active',
              desc: true,
            },
            {
              id: 'end',
              desc: true,
            },
          ],
          hiddenColumns: [
            'active',
            'startts',
            'endts',
          ],
        }}
        getCellProps={(cell, row) => {
          const { active } = row.values;

          if (!active) {
            return {
              style: {
                fontWeight: 'normal',
                color: APP_THEME.colors.muted,
              },
            };
          }

          return {
            style: {
              fontWeight: 'bold',
              color: '#000000',
            },
          };
        }}
      />
    </>
  );
};

TopicFocusTable.displayName = 'TopicFocusTable';

export default TopicFocusTable;
